import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Upload,
  Mail,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
  XCircle,
  HardDrive,
  UserPlus,
  Send,
  Copy,
  Check,
  Loader2,
  Settings,
  Eye,
  EyeOff,
  ClipboardList,
  CheckCircle2,
  Circle,
  Database,
  FolderLock,
  ShieldCheck,
  Lock,
  Ban,
  Archive,
  AlertTriangle,
  Palette,
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@connor-music/shared';
import { DataTable } from '@shared/components/DataTable';
import { StatusBadge } from '@shared/components/StatusBadge';
import { Modal } from '@shared/components/Modal';
import { FormField } from '@shared/components/FormField';
import { EmptyState } from '@shared/components/EmptyState';
import { ConfirmDialog } from '@shared/components/ConfirmDialog';
import { Skeleton } from '@shared/components/Skeleton';
import type { Column } from '@shared/components/DataTable';
import type { Project, Customer, FileUpload, Invitation, Platform, EmailTrackingEvent } from '@shared/types';
import { ProjectBrandingConfig } from '@/components/ProjectBrandingConfig';
import toast from 'react-hot-toast';

type Tab = 'project-data' | 'branding' | 'customers' | 'uploads' | 'invitations' | 'provisioning';

type ProvisioningStatus = 'completed' | 'failed' | 'deploying' | 'not_started';

interface ProvisioningStep {
  title: string;
  description: string;
  icon: React.ElementType;
  status: ProvisioningStatus;
  timestamp: string | null;
}

type CustomerRow = Customer & Record<string, unknown>;
type UploadRow = FileUpload & Record<string, unknown>;
type InvitationRow = Invitation & Record<string, unknown>;

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const [project, setProject] = useState<Project | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('project-data');
  const [isLoading, setIsLoading] = useState(true);

  // Storage retry state
  const [isRetryingStorage, setIsRetryingStorage] = useState(false);

  // Bucket reveal/copy state
  const [bucketRevealed, setBucketRevealed] = useState(false);
  const [copiedBucket, setCopiedBucket] = useState(false);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    variant: 'danger' | 'warning';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    variant: 'danger',
    onConfirm: () => {},
  });

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Downloading state
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Upload status filter
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCleaningStale, setIsCleaningStale] = useState(false);

  const loadProject = useCallback(async () => {
    if (!id) return;
    try {
      const [proj, custs, upls, invs, plats] = await Promise.all([
        apiClient.getProject(id),
        apiClient.getProjectCustomers(id),
        apiClient.getProjectUploads(id),
        apiClient.getProjectInvitations(id),
        apiClient.getPlatforms().catch(() => [] as Platform[]),
      ]);
      setProject(proj);
      setCustomers(custs);
      setUploads(upls);
      setInvitations(invs);
      setPlatforms(plats);
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project details');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handleCopyBucket = async () => {
    if (!project?.s3_bucket_name) return;
    try {
      await navigator.clipboard.writeText(project.s3_bucket_name);
      setCopiedBucket(true);
      setTimeout(() => setCopiedBucket(false), 2000);
    } catch {
      toast.error('Failed to copy bucket name');
    }
  };

  const handleRetryStorage = async () => {
    if (!id) return;
    setIsRetryingStorage(true);
    try {
      const updated = await apiClient.retryProjectStorage(id);
      setProject(updated);
      toast.success('Storage provisioned successfully');
    } catch {
      toast.error('Failed to provision storage. Please try again.');
    } finally {
      setIsRetryingStorage(false);
    }
  };

  const handleEditProject = async (data: { name: string; description: string; status: string; platform_id?: string; due_date?: string }) => {
    if (!id) return;
    try {
      const updated = await apiClient.updateProject(id, {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        status: data.status as 'awaiting_upload' | 'in_review' | 'completed' | 'archived',
        platform_id: data.platform_id,
        due_date: data.due_date,
      });
      setProject(updated);
      setShowEditModal(false);
      toast.success('Project updated successfully');
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project');
    }
  };

  const handleDeleteProject = () => {
    if (!id || !project) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await apiClient.deleteProject(id);
      toast.success('Project deleted. S3 data will be retained for 30 days before automatic deletion.');
      window.location.href = '/projects';
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleCleanupStale = async () => {
    if (!id) return;
    setIsCleaningStale(true);
    try {
      const result = await apiClient.cleanupStaleUploads(id);
      if (result.marked_failed > 0) {
        toast.success(`Marked ${result.marked_failed} stale upload${result.marked_failed !== 1 ? 's' : ''} as failed`);
        const upls = await apiClient.getProjectUploads(id);
        setUploads(upls);
      } else {
        toast.success('No stale uploads found');
      }
    } catch {
      toast.error('Failed to clean up stale uploads');
    } finally {
      setIsCleaningStale(false);
    }
  };

  const openEditModal = () => {
    if (!project) return;
    setShowEditModal(true);
  };

  const handleInviteCustomer = async (data: { email: string; company_name: string; given_name: string; family_name: string }) => {
    if (!id) return;
    try {
      await apiClient.createProjectInvitation(id, data);
      toast.success('Invitation sent successfully');
      setShowInviteModal(false);
      // Refresh both invitations and customers (backend creates customer record during invitation)
      const [invs, custs] = await Promise.all([
        apiClient.getProjectInvitations(id),
        apiClient.getProjectCustomers(id),
      ]);
      setInvitations(invs);
      setCustomers(custs);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error('Failed to send invitation');
    }
  };

  const handleRemoveCustomer = (customer: Customer) => {
    if (!id) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Customer',
      message: `Are you sure you want to remove ${customer.display_name} (${customer.email}) from this project?`,
      confirmLabel: 'Remove',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await apiClient.removeProjectCustomer(id, customer.id);
          toast.success('Customer removed from project');
          const custs = await apiClient.getProjectCustomers(id);
          setCustomers(custs);
        } catch (error) {
          console.error('Failed to remove customer:', error);
          toast.error('Failed to remove customer');
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDownload = async (upload: FileUpload) => {
    if (!id) return;
    setDownloadingId(upload.id);
    try {
      const { url } = await apiClient.getDownloadUrl(id, upload.sk);
      window.open(url, '_blank');
      toast.success(`Downloading ${upload.file_name}`);
    } catch (error) {
      console.error('Failed to get download URL:', error);
      toast.error('Failed to generate download link');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    if (!id) return;
    try {
      await apiClient.createProjectInvitation(id, {
        email: invitation.email,
        company_name: invitation.company_name,
        given_name: invitation.given_name,
        family_name: invitation.family_name,
      });
      toast.success(`Invitation resent to ${invitation.email}`);
      const invs = await apiClient.getProjectInvitations(id);
      setInvitations(invs);
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error('Failed to resend invitation');
    }
  };

  const handleCancelInvitation = (invitation: Invitation) => {
    if (!id) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Invitation',
      message: `Are you sure you want to cancel the invitation for ${invitation.email}?`,
      confirmLabel: 'Cancel Invitation',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await apiClient.updateInvitationStatus(id, invitation.id, 'cancelled');
          toast.success('Invitation cancelled');
          const invs = await apiClient.getProjectInvitations(id);
          setInvitations(invs);
        } catch (error) {
          console.error('Failed to cancel invitation:', error);
          toast.error('Failed to cancel invitation');
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const isOwnerOrAdmin = user && project && (user.is_connor_admin || user.id === project.owner_id);
  const canDownload = user?.download_allowed ?? false;
  const canDeleteProject = user?.delete_projects_allowed ?? false;

  // Column definitions
  const customerColumns: Column<CustomerRow>[] = [
    {
      key: 'display_name',
      label: 'Name',
      render: (_value, item) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {item.display_name as string}
        </span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (_value, item) => (
        <span className="text-gray-600 dark:text-gray-400">{item.email as string}</span>
      ),
    },
    {
      key: 'company_name',
      label: 'Company',
      render: (_value, item) => (
        <span className="text-gray-700 dark:text-gray-300">{item.company_name as string}</span>
      ),
    },
    {
      key: 'created_at',
      label: 'Assigned Date',
      render: (_value, item) => (
        <span className="text-gray-500 dark:text-gray-400">
          {formatDate(item.created_at as string)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, item) => (
        <button
          onClick={() => handleRemoveCustomer(item as unknown as Customer)}
          className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <Trash2 className="h-3 w-3 mr-1" />
          Remove
        </button>
      ),
    },
  ];

  const uploadColumns: Column<UploadRow>[] = [
    {
      key: 'file_name',
      label: 'File Name',
      render: (_value, item) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {item.file_name as string}
        </span>
      ),
    },
    {
      key: 'file_size',
      label: 'Size',
      render: (_value, item) => (
        <span className="text-gray-600 dark:text-gray-400">
          {formatFileSize(item.file_size as number)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_value, item) => (
        <StatusBadge status={item.status as string} type="upload" />
      ),
    },
    {
      key: 'uploaded_by_name',
      label: 'Uploaded By',
      render: (_value, item) => (
        <span className="text-gray-700 dark:text-gray-300">
          {(item.uploaded_by_name as string) || '—'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (_value, item) => (
        <span className="text-gray-500 dark:text-gray-400">
          {formatDate(item.created_at as string)}
        </span>
      ),
    },
    {
      key: 'download',
      label: '',
      render: (_value, item) => {
        if ((item.status as string) !== 'complete') return null;

        if (!canDownload) {
          return (
            <span
              title="Download access not enabled"
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-md cursor-not-allowed"
            >
              <Download className="h-3 w-3 mr-1" />
              Download
            </span>
          );
        }

        const isDownloading = downloadingId === (item.id as string);
        return (
          <button
            onClick={() => handleDownload(item as unknown as FileUpload)}
            disabled={isDownloading}
            className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-accent-600 dark:text-accent-400 hover:text-accent-800 dark:hover:text-accent-300 border border-accent-200 dark:border-accent-800 rounded-md hover:bg-accent-50 dark:hover:bg-accent-900/20 disabled:opacity-50 transition-colors"
          >
            <Download className="h-3 w-3 mr-1" />
            {isDownloading ? 'Preparing...' : 'Download'}
          </button>
        );
      },
    },
  ];

  const invitationColumns: Column<InvitationRow>[] = [
    {
      key: 'email',
      label: 'Recipient',
      render: (_value, item) => (
        <div>
          {((item.given_name as string) || (item.family_name as string)) && (
            <p className="font-medium text-gray-900 dark:text-white">
              {item.given_name as string} {item.family_name as string}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {item.email as string}
          </p>
        </div>
      ),
    },
    {
      key: 'invited_by_name',
      label: 'Invited By',
      render: (_value, item) => (
        <span className="text-gray-700 dark:text-gray-300">
          {(item.invited_by_name as string) || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_value, item) => (
        <StatusBadge status={item.status as string} type="invitation" />
      ),
    },
    {
      key: 'created_at',
      label: 'Sent',
      render: (_value, item) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {item.created_at ? formatDateTime(item.created_at as string) : '—'}
        </span>
      ),
    },
    {
      key: 'expires_at',
      label: 'Expires',
      render: (_value, item) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {item.expires_at ? formatDateTime(item.expires_at as string) : '—'}
        </span>
      ),
    },
    {
      key: 'email_status',
      label: 'Email Status',
      render: (_value, item) => {
        const events = (item.email_events as EmailTrackingEvent[] | undefined) || [];
        if (events.length === 0) {
          return <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>;
        }

        const hasBounce = events.some(e => e.type === 'bounce');
        const hasClick = events.some(e => e.type === 'click');
        const hasOpen = events.some(e => e.type === 'open');
        const hasDelivery = events.some(e => e.type === 'delivery');

        if (hasBounce) {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <XCircle className="h-3 w-3" /> Bounced
            </span>
          );
        }
        if (hasClick) {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-3 w-3" /> Clicked
            </span>
          );
        }
        if (hasOpen) {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
              <Mail className="h-3 w-3" /> Opened
            </span>
          );
        }
        if (hasDelivery) {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <Check className="h-3 w-3" /> Delivered
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Send className="h-3 w-3" /> Sent
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, item) => {
        if ((item.status as string) !== 'pending') return null;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleResendInvitation(item as unknown as Invitation)}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-accent-600 dark:text-accent-400 hover:text-accent-800 dark:hover:text-accent-300 border border-accent-200 dark:border-accent-800 rounded-md hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Resend
            </button>
            <button
              onClick={() => handleCancelInvitation(item as unknown as Invitation)}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <XCircle className="h-3 w-3 mr-1" />
              Cancel
            </button>
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-4 w-28 mb-4" />
        <div className="flex items-start gap-4 mb-6">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <Skeleton className="h-4 w-96 max-w-full" />
            <Skeleton className="h-6 w-72 max-w-full rounded-md" />
          </div>
        </div>
        <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700 mb-6 pb-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-24" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Project not found.</p>
        <Link to="/projects" className="mt-4 text-accent-600 hover:text-accent-700">
          Back to projects
        </Link>
      </div>
    );
  }

  // Derive provisioning steps from project data
  const storageProvisioned = !!project.s3_bucket_name;
  const storageStatus: ProvisioningStatus = isRetryingStorage
    ? 'deploying'
    : storageProvisioned
      ? 'completed'
      : 'failed';
  const storageTimestamp = storageProvisioned ? project.updated_at : null;

  const provisioningSteps: ProvisioningStep[] = [
    {
      title: 'Project Record Created',
      description: 'DynamoDB record for the audit project with metadata, owner, and status tracking.',
      icon: Database,
      status: 'completed',
      timestamp: project.created_at,
    },
    {
      title: 'S3 Bucket Created',
      description: 'Isolated S3 storage bucket provisioned for this project\'s audit files.',
      icon: FolderLock,
      status: storageStatus,
      timestamp: storageTimestamp,
    },
    {
      title: 'Versioning Enabled',
      description: 'S3 object versioning turned on to protect against accidental overwrites or deletions.',
      icon: Archive,
      status: storageStatus,
      timestamp: storageTimestamp,
    },
    {
      title: 'Encryption Configured',
      description: 'Server-side encryption (SSE-S3 / AES-256) applied to all objects in the bucket.',
      icon: Lock,
      status: storageStatus,
      timestamp: storageTimestamp,
    },
    {
      title: 'Public Access Blocked',
      description: 'All public access paths blocked — files are only accessible via presigned URLs.',
      icon: Ban,
      status: storageStatus,
      timestamp: storageTimestamp,
    },
    {
      title: 'Lifecycle Rules Applied',
      description: 'Automatic archival to S3 Glacier Deep Archive after 365 days for cost optimization.',
      icon: ShieldCheck,
      status: storageStatus,
      timestamp: storageTimestamp,
    },
  ];

  const completedSteps = provisioningSteps.filter((s) => s.status === 'completed').length;

  const tabs = [
    { key: 'project-data' as Tab, label: 'Project Data', icon: ClipboardList, count: -1 },
    { key: 'branding' as Tab, label: 'Project Branding', icon: Palette, count: -1 },
    { key: 'customers' as Tab, label: 'Customers', icon: Users, count: customers.length },
    { key: 'uploads' as Tab, label: 'Uploads', icon: Upload, count: uploads.length },
    { key: 'invitations' as Tab, label: 'Invitations', icon: Mail, count: invitations.length },
    { key: 'provisioning' as Tab, label: 'Provisioning', icon: Settings, count: -1 },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/projects"
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Projects
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0 flex-1 flex items-start gap-4">
            {(() => {
              const plat = platforms.find(p => p.id === project.platform_id);
              if (!plat) return null;
              const logoUrl = resolvedTheme === 'dark'
                ? (plat.logo_mark_dark_url || plat.logo_mark_light_url)
                : (plat.logo_mark_light_url || plat.logo_mark_dark_url);
              return logoUrl ? (
                <img
                  src={logoUrl}
                  alt={plat.name}
                  className="h-12 w-12 object-contain flex-shrink-0 mt-0.5"
                  title={plat.name}
                />
              ) : (
                <div
                  className="h-12 w-12 flex-shrink-0 mt-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"
                  title={plat.name}
                >
                  <span className="text-lg font-bold text-gray-400 dark:text-gray-500">
                    {plat.name.charAt(0)}
                  </span>
                </div>
              );
            })()}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                  {project.name}
                </h1>
                <StatusBadge status={project.status} type="project" />
                {project.due_date && (() => {
                  const isOverdue = project.due_date < new Date().toISOString().split('T')[0]
                    && (project.status === 'awaiting_upload' || project.status === 'in_review');
                  return (
                    <span className={`text-sm ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                      Due {formatDate(project.due_date)}
                      {isOverdue && ' (overdue)'}
                    </span>
                  );
                })()}
              </div>
              {project.description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{project.description}</p>
              )}
            </div>
          </div>
          {isOwnerOrAdmin && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={openEditModal}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </button>
              {canDeleteProject && (
                <button
                  onClick={handleDeleteProject}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
        <nav className="-mb-px flex space-x-6 sm:space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.key
                  ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
              {tab.key === 'provisioning' ? (
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                  completedSteps === provisioningSteps.length
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                }`}>
                  {completedSteps}/{provisioningSteps.length}
                </span>
              ) : tab.count >= 0 ? (
                <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full px-2 py-0.5 text-xs">
                  {tab.count}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'project-data' && project && (
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ClipboardList className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Project Metadata
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
              Project-specific metadata and configuration details will be managed here. Coming soon.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'branding' && project && (
        <ProjectBrandingConfig project={project} onUpdate={loadProject} />
      )}

      {activeTab === 'customers' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Project Customers
            </h3>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded-md transition-colors"
            >
              <UserPlus className="h-4 w-4 mr-1.5" />
              Invite Customer
            </button>
          </div>
          {customers.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <EmptyState
                icon={<Users className="h-12 w-12" />}
                title="No customers assigned"
                description="Invite a customer to this project to get started with the button above."
              />
            </div>
          ) : (
            <DataTable<CustomerRow>
              columns={customerColumns}
              data={customers as CustomerRow[]}
              emptyMessage="No customers assigned to this project."
            />
          )}
        </div>
      )}

      {activeTab === 'uploads' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Uploaded Files
            </h3>
            {user?.is_connor_admin && uploads.some((u) => u.status === 'pending' || u.status === 'uploading') && (
              <button
                onClick={handleCleanupStale}
                disabled={isCleaningStale}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isCleaningStale ? 'animate-spin' : ''}`} />
                {isCleaningStale ? 'Cleaning...' : 'Clean Up Stale'}
              </button>
            )}
          </div>
          {uploads.length > 0 && (
            <div className="mb-4">
              <label htmlFor="upload-status-filter" className="sr-only">Filter by status</label>
              <select
                id="upload-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-accent-500 focus:ring-accent-500 text-sm"
              >
                <option value="all">All Statuses</option>
                <option value="complete">Complete</option>
                <option value="pending">Pending</option>
                <option value="uploading">Uploading</option>
                <option value="failed">Failed</option>
                <option value="aborted">Aborted</option>
              </select>
            </div>
          )}
          {uploads.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <EmptyState
                icon={<Upload className="h-12 w-12" />}
                title="No files uploaded"
                description="Files will appear here once customers upload audit data to this project."
              />
            </div>
          ) : (
            <DataTable<UploadRow>
              columns={uploadColumns}
              data={(statusFilter === 'all' ? uploads : uploads.filter((u) => u.status === statusFilter)) as UploadRow[]}
              emptyMessage={statusFilter !== 'all' ? `No ${statusFilter} files found.` : 'No files match the selected filters.'}
            />
          )}
        </div>
      )}

      {activeTab === 'invitations' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Invitations
            </h3>
          </div>
          {invitations.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <EmptyState
                icon={<Mail className="h-12 w-12" />}
                title="No invitations sent"
                description="Invitations are automatically created when you invite a customer from the Customers tab."
              />
            </div>
          ) : (
            <DataTable<InvitationRow>
              columns={invitationColumns}
              data={invitations as InvitationRow[]}
              emptyMessage="No invitations for this project."
            />
          )}
        </div>
      )}

      {activeTab === 'provisioning' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Project Provisioning
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Infrastructure and security configuration applied when this project was created.
            </p>
          </div>

          {/* Storage Details card */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              Storage Details
            </h4>
            {project.s3_bucket_name ? (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/20">
                  <HardDrive className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Storage Bucket</p>
                  <p className="text-sm font-mono text-gray-900 dark:text-white">
                    {bucketRevealed
                      ? project.s3_bucket_name
                      : project.s3_bucket_name.substring(0, 14) + '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setBucketRevealed(!bucketRevealed)}
                    className="p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title={bucketRevealed ? 'Hide bucket name' : 'Show full bucket name'}
                  >
                    {bucketRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={handleCopyBucket}
                    className="p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Copy bucket name"
                  >
                    {copiedBucket ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
                <div className="flex items-center gap-3">
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Storage Not Provisioned</p>
                    <p className="text-xs text-red-600 dark:text-red-300/70 mt-0.5">
                      The storage bucket failed to create. Retry to complete the setup.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRetryStorage}
                  disabled={isRetryingStorage}
                  className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors flex-shrink-0"
                >
                  {isRetryingStorage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {isRetryingStorage ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            )}
          </div>

          {/* Provisioning Timeline card */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                Provisioning Steps
              </h4>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${
                  completedSteps === provisioningSteps.length ? 'bg-green-500' : 'bg-amber-500'
                }`} />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {completedSteps} of {provisioningSteps.length} completed
                </span>
              </div>
            </div>
            <div className="relative">
              {provisioningSteps.map((step, index) => {
                const isLast = index === provisioningSteps.length - 1;
                const StepIcon = step.icon;

                const statusConfig = {
                  completed: {
                    lineClass: 'bg-green-500',
                    icon: <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />,
                    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                    badgeLabel: 'Deployed',
                  },
                  failed: {
                    lineClass: 'bg-red-200 dark:bg-red-900/40',
                    icon: <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />,
                    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                    badgeLabel: 'Failed',
                  },
                  deploying: {
                    lineClass: 'bg-blue-200 dark:bg-blue-900/40',
                    icon: <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />,
                    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                    badgeLabel: 'Deploying',
                  },
                  not_started: {
                    lineClass: 'bg-gray-200 dark:bg-gray-700',
                    icon: <Circle className="h-4 w-4 text-gray-400 dark:text-gray-500" />,
                    badgeClass: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                    badgeLabel: 'Pending',
                  },
                }[step.status];

                return (
                  <div key={index} className="relative flex gap-4">
                    {/* Timeline line + dot */}
                    <div className="flex flex-col items-center">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                        step.status === 'completed'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : step.status === 'failed'
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : step.status === 'deploying'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-700'
                      }`}>
                        <StepIcon className={`h-4 w-4 ${
                          step.status === 'completed'
                            ? 'text-green-600 dark:text-green-400'
                            : step.status === 'failed'
                              ? 'text-red-600 dark:text-red-400'
                              : step.status === 'deploying'
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-400 dark:text-gray-500'
                        }`} />
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 flex-1 min-h-[24px] ${statusConfig.lineClass}`} />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-6'}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                              {step.title}
                            </h4>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.badgeClass}`}>
                              {statusConfig.icon}
                              {statusConfig.badgeLabel}
                            </span>
                          </div>
                          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                            {step.description}
                          </p>
                        </div>
                        <span className="ml-4 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                          {step.timestamp ? formatDate(step.timestamp) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Retry button if any step failed */}
            {storageStatus === 'failed' && (
              <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Storage provisioning failed. You can retry to complete the setup.
                  </p>
                  <button
                    onClick={handleRetryStorage}
                    disabled={isRetryingStorage}
                    className="inline-flex items-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50 transition-colors"
                  >
                    {isRetryingStorage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {isRetryingStorage ? 'Retrying...' : 'Retry Provisioning'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite customer modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite Customer"
      >
        <InviteCustomerForm
          onSubmit={handleInviteCustomer}
          onCancel={() => setShowInviteModal(false)}
        />
      </Modal>

      {/* Edit project modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Project"
      >
        <EditProjectForm
          project={project}
          onSubmit={handleEditProject}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* Confirm dialog (for customer removal, invitation cancellation) */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
      />

      {/* Delete project flow */}
      {showDeleteModal && project && (
        <DeleteProjectFlow
          projectName={project.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}

/* ─── Isolated form components (own state = no parent re-render on keystroke) ─── */

function InviteCustomerForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: { email: string; company_name: string; given_name: string; family_name: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        email: email.trim(),
        company_name: company.trim(),
        given_name: givenName.trim(),
        family_name: familyName.trim(),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-accent-500 focus:ring-accent-500 sm:text-sm';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Email Address" required htmlFor="invite-email">
        <input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@company.com" className={inputClass} required />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="First Name" required htmlFor="invite-given-name">
          <input id="invite-given-name" type="text" value={givenName} onChange={(e) => setGivenName(e.target.value)} placeholder="Jane" className={inputClass} required />
        </FormField>
        <FormField label="Last Name" required htmlFor="invite-family-name">
          <input id="invite-family-name" type="text" value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="Doe" className={inputClass} required />
        </FormField>
      </div>
      <FormField label="Company Name" required htmlFor="invite-company">
        <input id="invite-company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="e.g., Spotify" className={inputClass} required />
      </FormField>
      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded-md disabled:opacity-50 transition-colors">
          {isSubmitting ? 'Sending...' : 'Send Invitation'}
        </button>
      </div>
    </form>
  );
}

function EditProjectForm({
  project,
  onSubmit,
  onCancel,
}: {
  project: Project;
  onSubmit: (data: { name: string; description: string; status: string; platform_id?: string; due_date?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [status, setStatus] = useState<string>(project.status);
  const [platformId, setPlatformId] = useState(project.platform_id || '');
  const [dueDate, setDueDate] = useState(project.due_date || '');
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    apiClient.getPlatforms().then(setPlatforms).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ name, description, status, platform_id: platformId || undefined, due_date: dueDate || undefined });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-accent-500 focus:ring-accent-500 sm:text-sm';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Project Name" required htmlFor="edit-project-name">
        <input id="edit-project-name" type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required />
      </FormField>
      <FormField label="Description" htmlFor="edit-project-desc">
        <textarea id="edit-project-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={inputClass} />
      </FormField>
      <FormField label="Status" htmlFor="edit-project-status">
        <select id="edit-project-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
          <option value="awaiting_upload">Awaiting Upload</option>
          <option value="in_review">In Review</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </FormField>
      <FormField label="Due Date" htmlFor="edit-project-due-date">
        <input id="edit-project-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
      </FormField>
      {platforms.length > 0 && (
        <FormField label="Platform" htmlFor="edit-project-platform">
          <select id="edit-project-platform" value={platformId} onChange={(e) => setPlatformId(e.target.value)} className={inputClass}>
            <option value="">No platform</option>
            {platforms.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </FormField>
      )}
      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded-md disabled:opacity-50 transition-colors">
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

function DeleteProjectFlow({
  projectName,
  onConfirm,
  onCancel,
}: {
  projectName: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<'warning' | 'confirm_name'>('warning');
  const [confirmName, setConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmName !== projectName) return;
    setIsDeleting(true);
    await onConfirm();
  };

  if (step === 'warning') {
    return (
      <Modal isOpen onClose={onCancel} title="Delete Project" size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700 dark:text-red-300">
              <p className="font-medium mb-1">This action cannot be undone.</p>
              <p>This will remove the project and all associated data from the portal immediately. S3 files will be retained for 30 days before automatic deletion.</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
              Cancel
            </button>
            <button type="button" onClick={() => { setStep('confirm_name'); setConfirmName(''); }} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700">
              Continue
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen onClose={onCancel} title="Confirm Deletion" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          To confirm, type the project name{' '}
          <span className="font-semibold text-gray-900 dark:text-white">{projectName}</span>{' '}
          below.
        </p>
        <input
          type="text"
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          placeholder="Type project name to confirm"
          autoFocus
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
        />
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={confirmName !== projectName || isDeleting} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {isDeleting ? 'Deleting...' : 'Delete Project'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
